import { Button, message, Space, Spin, Typography } from "antd";
import { createRef, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import { PACKING_LIST_REPORT_VIEW_PERMISSION } from "../../../routes/permissions";
import { hasPermission } from "../../../utils/functions";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";

const BuyerPackingReport = () => {
  const exportRef = createRef(null);
  // States

  const [isPrint, setIsPrint] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [htmlResponse, setHtmlResponse] = useState(null);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  // Round 2 (B, R3): mounted-ref + request-sequence guards — BuyerPackingReportFilterFields
  // now also calls this (via form.submit()) once on mount for the default PO/style.
  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Antd
  const { Title } = Typography;

  const exportPacking = async (filterValues) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const query = `/api/report/view_packing_list_report`;
    const { po_map_id, style_id } = filterValues || {};
    const bodyData = {
      po_map_id,
      style_id,
    };
    const response = await postData(query, bodyData);

    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response && response?.data) {
      setLoading(false);
      setHtmlResponse(response?.data);
      message.success("Packing Report Exported Successfully");
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
    setHasFetchedOnce(true);
  };

  useEffect(() => {
    if (isPrint) {
      printFn();
      setIsPrint(false);
    }
  }, [isPrint]);

  const printFn = useReactToPrint({
    contentRef: exportRef,
    documentTitle: "Operation Bulletin",
    onAfterPrint: () => {
      setIsPrint(false);
    },
  });

  const previewFunction = async (filterValues) => {
    const query = `/api/report/generate_packing_list_report`;
    const { po_map_id, style_id } = filterValues || {};
    const bodyData = { po_map_id, style_id };

    try {
      const response = await getData(
        query,
        null,
        bodyData,
        { "Content-Type": "blob" },
        "arraybuffer",
      );

      if (response && response.data) {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "data.xlsx");
        document.body.appendChild(link);
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } else {
        message.error(response?.message || "Something went wrong");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch report");
    }
  };

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={[]}
        filterAPI={exportPacking}
        filterName={"buyer_packing_report"}
        searchButtonText={"Generate"}
        defaultOpen
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        isPreview
        previewFunction={previewFunction}
        preview="Download"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    setIsPrint(true);
                  }}
                  disabled={
                    !hasPermission([PACKING_LIST_REPORT_VIEW_PERMISSION])
                  }
                >
                  {hasPermission([PACKING_LIST_REPORT_VIEW_PERMISSION])
                    ? "Print"
                    : "No Permission"}
                </Button>
                {/* <Button
                  type="primary"
                  size="small"
                  onClick={() => showModal("excel")}
                  disabled={!hasPermission([OB_REPORT_PERMISSION])}
                >
                  {hasPermission([PACKING_CREATE_PERMISSION])
                    ? "Download Excel"
                    : "No Permission"}
                </Button> */}
                {/* <Button
                  type="primary"
                  size="small"
                  onClick={() => showModal("pdf")}
                  disabled={!hasPermission([OB_REPORT_PERMISSION])}
                >
                  {hasPermission([PACKING_CREATE_PERMISSION])
                    ? "Download PDF"
                    : "No Permission"}
                </Button> */}
              </Space>
            }
            title={<Title level={5}>Buyer Packing Report</Title>}
          />
        }
        content={
          <>
            <div className="table" style={{ marginTop: 10 }}>
              {memoizedCustomFilter}

              <Spin spinning={loading}>
                {!loading && hasFetchedOnce && !htmlResponse ? (
                  // Round 2 (B, R3): A3 empty-state hint — the mount-time default
                  // PO/style returned no report content.
                  <div style={{ padding: "24px 0", textAlign: "center", color: "rgba(0, 0, 0, 0.45)" }}>
                    No packing report found for the selected PO. Try a different PO.
                  </div>
                ) : (
                  <div
                    ref={exportRef}
                    dangerouslySetInnerHTML={{ __html: htmlResponse }}
                    // style={{ height: "100vh" }}
                  />
                )}
              </Spin>
            </div>
          </>
        }
      />
    </>
  );
};

export default BuyerPackingReport;
